// neo_cogym — WebSerial 전송 (P2: 데스크톱/크롬북/웨일북)
// Web Serial API 로 코드위즈(ESP32) USB 직결. iPadOS Safari 미지원(→ 릴레이 사용).
// T1 분석의 DTR/RTS 부트시퀀스·보레이트를 재현.

import type { Transport, TransportKind } from "./transport";
import { BAUD } from "./transport";

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class WebSerialTransport implements Transport {
  readonly kind: TransportKind = "webserial";
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private listeners = new Set<(c: Uint8Array) => void>();
  private _open = false;

  get isOpen() {
    return this._open;
  }

  static get supported() {
    return typeof navigator !== "undefined" && "serial" in navigator;
  }

  constructor(private baudRate: number = BAUD.repl) {}

  async open(): Promise<void> {
    if (!WebSerialTransport.supported) {
      throw new Error("WebSerial 미지원 브라우저 — 릴레이 연결을 사용하세요.");
    }
    // 사용자 제스처에서 포트 선택 필요
    this.port = await navigator.serial.requestPort();
    await this.port.open({ baudRate: this.baudRate });

    // 부트 시퀀스(T1: dtr/rts 토글) — ESP32 펌웨어 실행 진입
    await this.setSignals({ dataTerminalReady: false, requestToSend: true });
    await delay(200);
    await this.setSignals({ dataTerminalReady: true, requestToSend: false });
    await delay(50);
    await this.setSignals({ dataTerminalReady: false, requestToSend: false });

    this.writer = this.port.writable!.getWriter();
    this._open = true;
    this.pump();
  }

  private async setSignals(s: SerialOutputSignals) {
    try {
      await this.port?.setSignals(s);
    } catch {
      /* 일부 환경에서 미지원 — 무시 */
    }
  }

  private async pump() {
    const readable = this.port?.readable;
    if (!readable) return;
    this.reader = readable.getReader();
    try {
      while (this._open) {
        const { value, done } = await this.reader.read();
        if (done) break;
        if (value) for (const cb of this.listeners) cb(value);
      }
    } catch {
      /* 연결 종료 시 read 예외 무시 */
    } finally {
      this.reader?.releaseLock();
      this.reader = null;
    }
  }

  async write(data: Uint8Array): Promise<void> {
    if (!this.writer) throw new Error("포트가 열리지 않음");
    await this.writer.write(data);
  }

  onData(cb: (c: Uint8Array) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  async close(): Promise<void> {
    this._open = false;
    try {
      void this.reader?.cancel();
    } catch {
      /* noop */
    }
    try {
      this.reader?.releaseLock();
    } catch {
      /* noop */
    }
    try {
      this.writer?.releaseLock();
    } catch {
      /* noop */
    }
    this.reader = null;
    this.writer = null;
    await delay(200);
    try {
      await this.port?.close();
    } catch {
      /* noop */
    }
    this.port = null;
    this.listeners.clear();
  }
}
